import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import React from "react"
import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import Select from "@/components/Common/AppSelect"
import type { ApiError } from "../../client"
import { type Role, type UserCreate, UsersService } from "../../client"
import useCustomToast from "../../hooks/useCustomToast"
import { emailPattern, handleError } from "../../utils"

interface AddUserProps {
  isOpen: boolean
  onClose: () => void
}

interface UserCreateForm extends UserCreate {
  confirm_password: string
  roleCode: string
  roleLevel: string
}

const AddUser = ({ isOpen, onClose }: AddUserProps) => {
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const [show, setShow] = React.useState(false)
  const [showConfirm, setShowConfirm] = React.useState(false)
  const handleClick = () => setShow(!show)
  const handleClickConfirm = () => setShowConfirm(!showConfirm)
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UserCreateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      email: "",
      fullName: "",
      acronym: "",
      password: "",
      confirm_password: "",
      roles: [],
      roleCode: "",
      roleLevel: "",
      isActive: true,
    },
  })
  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: () => UsersService.readRoles(),
  })

  const mutation = useMutation({
    mutationFn: (data: UserCreate) =>
      UsersService.createUser({ requestBody: data }),
    onSuccess: () => {
      showToast("Sukces!", "Użytkownik został utworzony.", "success")
      reset()
      onClose()
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })

  const sanitizeAcronym = (v: string) =>
    v
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 5)

  const rolesByLevel = React.useMemo(() => {
    const grouped: Record<string, Role[]> = {}
    ;(roles || []).forEach((r) => {
      const level = (r.level || "").toLowerCase()
      if (!grouped[level]) grouped[level] = []
      grouped[level].push(r)
    })
    return grouped
  }, [roles])

  const availableLevels = React.useMemo(
    () => Object.keys(rolesByLevel),
    [rolesByLevel],
  )

  const selectedLevel = watch("roleLevel")

  React.useEffect(() => {
    if (!selectedLevel && availableLevels.length > 0) {
      const first = availableLevels[0]
      setValue("roleLevel", first, { shouldValidate: true })
      setValue("roleCode", "", { shouldValidate: true })
    }
  }, [availableLevels, selectedLevel, setValue])

  const onSubmit: SubmitHandler<UserCreateForm> = (data) => {
    const email = data.email.trim()
    const fullName = data.fullName?.trim() || ""
    const rawAcr = data.acronym ? sanitizeAcronym(data.acronym) : ""
    const roleCode = data.roleCode?.trim().toUpperCase()

    const payload: UserCreate = {
      email,
      fullName,
      password: data.password,
      roles: roleCode ? [roleCode] : [],
      isActive: data.isActive,
      ...(rawAcr ? { acronym: rawAcr } : {}),
    }

    mutation.mutate(payload)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "sm", md: "md" }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ModalHeader>Dodaj użytkownika</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel htmlFor="email">Email</FormLabel>
            <Input
              size="sm"
              id="email"
              {...register("email", {
                required: "Email jest wymagany",
                pattern: emailPattern,
              })}
              placeholder="Email"
              type="email"
            />
            {errors.email && (
              <FormErrorMessage>{errors.email.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl mt={4} isInvalid={!!errors.fullName}>
            <FormLabel htmlFor="name">Imię i nazwisko</FormLabel>
            <Input
              size="sm"
              id="name"
              {...register("fullName")}
              placeholder="Imię i nazwisko"
              type="text"
            />
            {errors.fullName && (
              <FormErrorMessage>{errors.fullName.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl mt={4} isInvalid={!!errors.acronym}>
            <FormLabel htmlFor="acronym">Akronim</FormLabel>
            <Input
              size="sm"
              id="acronym"
              maxLength={5}
              placeholder="Akronim (dokładnie 5 znaków lub pozostaw puste)"
              type="text"
              {...register("acronym", {
                onChange: (e) => {
                  const val = sanitizeAcronym(e.target.value)
                  setValue("acronym", val, { shouldValidate: true })
                },
                validate: (v) => {
                  if (!v) return true
                  return (
                    /^[A-Z0-9]{5}$/.test(v) ||
                    "Akronim musi mieć dokładnie 5 znaków alfanumerycznych"
                  )
                },
              })}
            />
            {errors.acronym && (
              <FormErrorMessage>{errors.acronym.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl mt={4} isRequired isInvalid={!!errors.password}>
            <FormLabel htmlFor="password">Ustaw hasło</FormLabel>
            <InputGroup size="sm">
              <Input
                id="password"
                {...register("password", {
                  required: "Hasło jest wymagane",
                  minLength: {
                    value: 8,
                    message: "Hasło musi mieć co najmniej 8 znaków",
                  },
                })}
                placeholder="Hasło"
                pr="4.5rem"
                type={show ? "text" : "password"}
              />
              <InputRightElement width="4.5rem">
                <Button
                  type="button"
                  h="1.75rem"
                  variant="ghost"
                  size="xs"
                  onClick={handleClick}
                  aria-label={show ? "Ukryj hasło" : "Pokaż hasło"}
                >
                  {show ? <ViewOffIcon /> : <ViewIcon />}
                </Button>
              </InputRightElement>
            </InputGroup>
            {errors.password && (
              <FormErrorMessage>{errors.password.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl mt={4} isRequired isInvalid={!!errors.confirm_password}>
            <FormLabel htmlFor="confirm_password">Potwierdź hasło</FormLabel>
            <InputGroup size="sm">
              <Input
                id="confirm_password"
                {...register("confirm_password", {
                  required: "Potwierdź hasło",
                  validate: (value) =>
                    value === getValues().password || "Hasła nie są zgodne",
                })}
                placeholder="Hasło"
                pr="4.5rem"
                type={showConfirm ? "text" : "password"}
              />
              <InputRightElement width="4.5rem">
                <Button
                  type="button"
                  h="1.75rem"
                  size="xs"
                  variant="ghost"
                  onClick={handleClickConfirm}
                  aria-label={
                    showConfirm
                      ? "Ukryj hasło potwierdzenia"
                      : "Pokaż hasło potwierdzenia"
                  }
                >
                  {showConfirm ? <ViewOffIcon /> : <ViewIcon />}
                </Button>
              </InputRightElement>
            </InputGroup>
            {errors.confirm_password && (
              <FormErrorMessage>
                {errors.confirm_password.message}
              </FormErrorMessage>
            )}
          </FormControl>
          <FormControl mt={4}>
            <FormLabel htmlFor="roleLevel">Poziom</FormLabel>
            {rolesLoading ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm">Ładowanie poziomów...</Text>
              </Flex>
            ) : (
              <Select
                size="sm"
                id="roleLevel"
                placeholder="Wybierz poziom"
                value={selectedLevel}
                onChange={(e) => {
                  setValue("roleLevel", e.target.value, {
                    shouldValidate: true,
                  })
                }}
              >
                {availableLevels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </Select>
            )}
            <FormHelperText>
              Najpierw wybierz poziom (user/admin/superadmin).
            </FormHelperText>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.roleCode}>
            <FormLabel htmlFor="roleCode">Rola</FormLabel>
            {rolesLoading ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm">Ładowanie ról...</Text>
              </Flex>
            ) : (
              <Controller
                control={control}
                name="roleCode"
                rules={{
                  required: "Rola jest wymagana",
                  validate: () => {
                    if (!selectedLevel) return "Najpierw wybierz poziom"
                    return true
                  },
                }}
                render={({ field }) => {
                  const levelRoles = selectedLevel
                    ? rolesByLevel[selectedLevel] || []
                    : []
                  return (
                    <Stack spacing={2}>
                      {levelRoles.length === 0 && (
                        <Text fontSize="sm" color="gray.500">
                          Brak ról dla wybranego poziomu.
                        </Text>
                      )}
                      {levelRoles.map((role: Role) => (
                        <Box
                          key={role.code}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          cursor="pointer"
                          bg={
                            field.value === role.code
                              ? "teal.50"
                              : "transparent"
                          }
                          borderColor={
                            field.value === role.code ? "teal.300" : "gray.200"
                          }
                          onClick={() => field.onChange(role.code)}
                          _hover={{ borderColor: "teal.200" }}
                        >
                          <Flex align="center" justify="space-between">
                            <Box>
                              <Text fontWeight="medium">{role.code}</Text>
                              <Text fontSize="xs" color="gray.500">
                                Poziom: {role.level}
                              </Text>
                            </Box>
                            {field.value === role.code && (
                              <Box color="teal.500">
                                <ViewIcon />
                              </Box>
                            )}
                          </Flex>
                        </Box>
                      ))}
                    </Stack>
                  )
                }}
              />
            )}
            <FormHelperText>Wybierz rolę dla wybranego poziomu.</FormHelperText>
            {errors.roleCode && (
              <FormErrorMessage>
                {errors.roleCode.message as string}
              </FormErrorMessage>
            )}
          </FormControl>
          <Flex mt={4}>
            <FormControl>
              <Checkbox {...register("isActive")} colorScheme="teal">
                Aktywne konto?
              </Checkbox>
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            isLoading={isSubmitting}
          >
            Zapisz
          </Button>
          <Button size="sm" onClick={onClose}>
            Anuluj
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddUser
